import PropTypes from 'prop-types'
import React, { useState, useEffect } from 'react'
import {
  Button,
  Container,
  Grid,
  Header,
  List,
  Segment,
  Dropdown,
  Input
} from 'semantic-ui-react'

import RunModal from 'components/analysis/RunModal';
import LeverButton, {LeverButtonComponents} from 'components/analysis/LeverButton';
import ResponsiveContainer from 'components/layout/PageComponents';
import styled from 'styled-components';

const adaptations = {
  hazard: [
    {
      text: 'Conventional flood control',
      enabled: true,
      state: 'C',
      info: 'Sample info about conventional flood control. <br /><br />(L=Lower, H=Higher, C=Central)'
    },
    {
      text: 'Nature based catchment management',
      enabled: true,
      state: 'C',
      info: 'Different help!'
    },
    {
      text: 'Green and blue urban infrastructure',
      enabled: true,
      state: 'C',
      color: 'blue'
    },
    {
      text: 'Conventional urban drainage',
      enabled: true,
      state: 'C'
    }
  ],
  exposure: [
    {
      text: 'Spatial planning',
      enabled: true,
      state: 'C'
    }
  ],
  vulnerability: [
    {
      text: 'Residential property resilience',
      state: 'C',
      info: 'Some sample info'
    },
    {
      text: 'Business property resilience',
      state: 'C'
    },
    {
      text: 'Infrastructure service resilience',
      state: 'C'
    },
    {
      text: 'Forecasting and warning',
      state: 'C'
    },
    {
      text: 'Insurance and compensation',
      state: 'C'
    },
  ]
};

const EpochInput = styled(Input)`
  borderRadius: 0;
  input {
    text-align: center !important;
    border-radius: 0 !important;
    font-size: 14px !important;
    height: 3em;
  }
`;

const EpochButton = (props) => {
  const theme = LeverButtonComponents.themes.grey;
  return (
    <LeverButtonComponents.LeverButton>
      <LeverButtonComponents.TextBox theme={theme}>{props.text}</LeverButtonComponents.TextBox>
      <Button.Group>
        <LeverButtonComponents.AttachedButton theme={theme} onClick={() => props.setValue(props.value-1)}>
          -
        </LeverButtonComponents.AttachedButton>
        <div style={{width: "64px"}}>
          <EpochInput fluid value={props.value} onChange={(e, {value}) => props.setValue(Number(value))}/>
        </div>
        <LeverButtonComponents.AttachedButton theme={theme}
                                              onClick={() => props.setValue(props.value+1)}
        >+</LeverButtonComponents.AttachedButton>
      </Button.Group>
    </LeverButtonComponents.LeverButton>
  );
};

const RunButtonContainer = styled.div`
  position: sticky;
  float: right;
  z-index: 999;
  top: 80px;
  margin-top: -60px
`;

const HomepageLayout = (props) => {
  const [adaptationState, setAdaptationState] = useState(adaptations);
  const [climateFuture, setClimateFuture] = useState('C');
  const [socioeconomicFuture, setSocioeconomicFuture] = useState('C');
  const [startEpoch, setStartEpoch] = useState(2018);
  const [endEpoch, setEndEpoch] = useState(2037);
  const [epochInterval, setEpochInterval] = useState(1);
  const [modalShowing, setModalShowing] = useState(false);
  const [defaultRunName, setDefaultRunName] = useState("test");

  useEffect(() => {
    setDefaultRunName(`${startEpoch}-${endEpoch}_climate${climateFuture}_socioeconomic${socioeconomicFuture}`)
  },
  [startEpoch, endEpoch, climateFuture, socioeconomicFuture]);

  return (
    <React.Fragment>
    <RunModal isShowing={modalShowing} onHide={() => setModalShowing(false)} defaultJobName={defaultRunName} />
    <ResponsiveContainer height={100} currentPage="analysis">
      <Segment style={{ padding: '3em 0em' }} vertical>
        <Grid container stackable verticalAlign='middle'>
          <Grid.Row>
            <Grid.Column width={16}>
              <Header as='h2' style={{ fontSize: '2em' }}>
                FFE scenario builder
              </Header>

              <RunButtonContainer>
                <Button primary size='huge' onClick={()=>setModalShowing(true)}>Run</Button>
              </RunButtonContainer>

              <Header as='h4' style={{fontSize: '1.5em'}} >
                Time horizons
              </Header>

              <Grid columns={6}>
                <Grid.Row stretched>
                  <Grid.Column>
                    <EpochButton text="Start epoch" value={startEpoch} setValue={setStartEpoch} />
                  </Grid.Column>
                  <Grid.Column>
                    <EpochButton text="End epoch" value={endEpoch} setValue={setEndEpoch} />
                  </Grid.Column>
                  <Grid.Column>
                    <EpochButton text="Intervals" value={epochInterval} setValue={setEpochInterval} />
                  </Grid.Column>
                </Grid.Row>
              </Grid>

              <Header as='h4' style={{fontSize: '1.5em'}} >
                Exogenous changes
              </Header>

              <Grid columns={6}>
                <Grid.Row stretched>
                  <Grid.Column>
                    <LeverButton
                      theme="blue"
                      text="Climate change"
                      state={climateFuture}
                      info="Climate change info"
                      handleClick={(lever) => setClimateFuture(lever)}
                    />
                  </Grid.Column>
                  <Grid.Column>
                    <LeverButton
                      theme="blue"
                      text="Socio-economic change"
                      state={socioeconomicFuture}
                      info="<strong>sustainable growth</strong>: this encapsulates achievement of key government objectives and strategies, resulting in high economic growth, strong social progress and the achievement of environmental targets<br/><br />
<strong>conservative growth</strong>: recent historical trends continue, such that progress is broadly consistent with key national plans and there is robust economic growth, improved access to social services and slowdown of environmental degradation<br /><br />
<strong>regional instability</strong>: a challenging international macroeconomic environment threaten economic growth, compromising the government’s ability to deliver social progress or environmental protection."
                      handleClick={(lever) => setSocioeconomicFuture(lever)}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
              <br/>

              <Header as='h4' style={{ fontSize: '1.5em' }}>
                Adaptation Ambition (for future development)
              </Header>

              <h5><strong>Hazard:</strong> reducing the chance of flooding</h5>

              <Grid columns={6}>
                <Grid.Row stretched>
                  {adaptationState.hazard.map((hazardButton, index) => {
                    return (
                      <Grid.Column key={index}>
                        <LeverButton
                          theme="blue"
                          text={hazardButton.text}
                          state={hazardButton.state}
                          info={hazardButton.info}
                          handleClick={(lever) => {
                            const newState = Object.assign({}, adaptationState);
                            newState.hazard[index].state = lever;
                            setAdaptationState(newState);
                          }}
                        />
                      </Grid.Column>
                    )
                  })}

                </Grid.Row>
              </Grid>

              <div style={{height: '25px'}}/>

              <h5><strong>Exposure:</strong> managing the exposure to flooding</h5>
              <Grid columns={6}>
                <Grid.Row stretched>
                  {adaptationState.exposure.map((hazardButton, index) => {
                    return (
                      <Grid.Column key={index}>
                        <LeverButton
                          theme="orange"
                          text={hazardButton.text}
                          state={hazardButton.state}
                          handleClick={(lever) => {
                            const newState = Object.assign({}, adaptationState);
                            newState.exposure[index].state = lever;
                            setAdaptationState(newState);
                          }}
                        />
                      </Grid.Column>
                    )
                  })}
                </Grid.Row>
              </Grid>

              <div style={{height: '25px'}}/>

              <h5><strong>Vulnerability:</strong> reducing the vulnerability of the exposed</h5>
              <Grid columns={6}>
                <Grid.Row stretched>
                  {adaptationState.vulnerability.map((hazardButton, index) => {
                    return (
                      <Grid.Column key={index}>
                        <LeverButton
                          theme="purple"
                          text={hazardButton.text}
                          state={hazardButton.state}
                          handleClick={(lever) => {
                            const newState = Object.assign({}, adaptationState);
                            newState.exposure[index].state = lever;
                            setAdaptationState(newState);
                          }}
                        />
                      </Grid.Column>
                    )
                  })}

                </Grid.Row>
              </Grid>
            </Grid.Column>
          </Grid.Row>
        </Grid>


        {
          // TODO: RUN button instead of  login and sign up
          //     -- progress bar in modal
        }   </Segment>
      <Segment inverted vertical style={{ padding: '5em 0em' }}>
        <Container>
          <Grid divided inverted stackable>
            <Grid.Row>
              <Grid.Column width={3} />
              <Grid.Column width={3}>
                <Header inverted as='h4' content='About' />
                <List link inverted>
                  <List.Item as='a'>Sitemap</List.Item>
                  <List.Item as='a' href="http://http://www.sayersandpartners.co.uk/contact-us.html">Contact Us</List.Item>
                </List>
              </Grid.Column>
              <Grid.Column width={7}>
                <Header as='h4' inverted>
                  Bangladesh Flood Event Relief costs assessment
                </Header>
                <p>
                </p>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Container>
      </Segment>
    </ResponsiveContainer>
    </React.Fragment>
  )
}
export default HomepageLayout